import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { FaXTwitter } from 'react-icons/fa6';
import { DarkVeil } from '../components';

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [xLink, setXLink] = useState('');

    useEffect(() => {
        const fetchXLink = async () => {
            try {
                const res = await api.getCompanyInfo();
                const link = res?.data?.data?.xLink || '';
                setXLink(link);
            } catch (e) {
                console.error('Error fetching company info:', e);
            }
        };
        fetchXLink();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error(t('login.toastFillAll'));
            return;
        }

        setLoading(true);
        setError('');
        try {
            const credentials = {
                email: email,
                password: password
            };

            const result = await api.loginUser(credentials);
            console.log('Login successful:', result);

            // 2FA kontrolü - eğer 2FA aktif ise api.js'te yönlendirme yapılacak
            if (result && result.data && result.data.isTwoFactorEnabled === true) {
                // 2FA aktif ise burada hiçbir şey yapma, api.js'te yönlendirme yapıldı
                return;
            }

            // 2FA kapalı ise normal login flow
            if (result && result.data && result.data.isTwoFactorEnabled === false) {
                toast.success(t('login.toastSuccess'));

                // Response'dan token'ı al
                const token = result.data?.token || result.token || result.accessToken || result.jwt;
                console.log('Token extracted:', token);

                if (token) {
                    localStorage.setItem('auth:token', token);
                    console.log('Token saved to localStorage');
                }

                login({
                    id: result.data?.userId || result.userId || result.id || 'user',
                    name: result.data?.name || result.name || result.fullName || email,
                    email: email
                }, token);

                // Hesap özeti sayfasına yönlendir
                navigate('/account-summary');
            }
        } catch (err) {
            console.error('Login failed:', err);
            const errorMessage = err.message || t('login.toastError');
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="new-login-page">
            <div className="new-login-container">
                <div className="new-login-left">
                    <div className="new-login-left-animation">
                        <DarkVeil
                            colors={["#008cffff", "#9b00afff", "#00ffb3ff"]}
                            rotation={0}
                            speed={0.2}
                            scale={1}
                            frequency={1}
                            warpStrength={1}
                            mouseInfluence={1}
                            parallax={0.5}
                            noise={0.1}
                            transparent
                            autoRotate={0}
                        />
                    </div>
                    <div className="new-login-left-content">
                        <h2 className="new-login-left-title">
                            {t('login.newLeftTitle')}
                        </h2>
                        <p className="new-login-left-subtitle">
                            {t('login.newLeftSubtitle')}
                        </p>
                        <div className="new-login-social">
                            {xLink && (
                                <a href={xLink} target="_blank" rel="noopener noreferrer">
                                    <FaXTwitter size={20} />
                                    X
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <div className="new-login-right">
                    {/* <div className="new-login-right-header">
                        <Link to="/register" className="new-login-signup-btn">
                            {t('login.signUp')}
                        </Link>
                    </div> */}
                    <div className="new-login-form-container">
                        <div className="new-login-logo">
                            <img 
                                src="/assets/images/nordex_login_image.png" 
                                alt="Nordex Login" 
                                className="login-brand-image"
                            />
                        </div>
                        <form className="new-login-form" onSubmit={onSubmit}>
                            <div className="new-login-input-group">
                                <label htmlFor="email">{t('login.emailLabel')}</label>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder={t('login.emailPlaceholder')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
                                    onKeyDown={(e) => {
                                        if (e.key === ' ') {
                                            e.preventDefault();
                                        }
                                    }}
                                    required
                                />
                            </div>
                            <div className="new-login-input-group">
                                <label htmlFor="password">{t('login.passwordLabel')}</label>
                                <div className="new-login-password-wrapper">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('login.passwordPlaceholder')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="new-login-password-toggle"
                                        onClick={() => setShowPassword((s) => !s)}
                                    >
                                        {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                className="new-login-submit-btn"
                                disabled={loading}
                            >
                                {loading ? t('login.submitting') : t('login.title')}
                            </button>
                            <div className="new-login-forgot">
                                <Link to="/forgot-password">
                                    {t('login.forgot')}
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        style: {
                            background: '#10B981',
                            color: '#fff',
                        },
                    },
                    error: {
                        duration: 5000,
                        style: {
                            background: '#EF4444',
                            color: '#fff',
                        },
                    },
                }}
            />
        </div>
    );
}


